<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="VNコネクト選択"
  >
    <template v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col>
                <h3>VNコネクトタイプを選択</h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div class="mb-1">
                  VNコネクト作成時のタイプを選択することができます。
                </div>
                <ul class="pl-4">
                  <li>無料枠利用</li>
                  <li>通常利用</li>
                </ul>
                <div class="text-caption">
                  ※アクセス回線申込時の契約帯域によって料金は発生するため、VNコネクト作成の可否で料金の加減は発生しません。
                </div>
              </v-col>
            </v-row>

            <v-row>
              <!-- 無料枠利用 -->
              <v-col cols="6">
                <v-card
                  variant="outlined"
                  class="h-100"
                  :color="billingFreeUsage ? 'grey' : undefined"
                >
                  <v-card-text class="text-center">
                    <v-icon
                      icon="$mdiTransitConnection"
                      size="50"
                      class="vnConnectType"
                    />

                    <div class="mt-2 font-weight-bold text-subtitle-1">
                      無料枠利用
                    </div>
                    <div class="text-body-2 mb-2">
                      1Gbpsまで作成可能なVNコネクト
                    </div>

                    <v-btn
                      class="mt-1 freeBtnStyle"
                      @click="freeRegister"
                      :color="billingFreeUsage ? 'grey' : undefined"
                      :disabled="billingFreeUsage"
                    >
                      無料枠作成
                    </v-btn>

                    <ul class="ml-2 mt-2 text-left">
                      <li>
                        マルチクラウドGW契約ごとに1回線まで作成可能（帯域1Gbpsまで）
                      </li>
                      <li>マルチクラウドGW契約帯域が0Mbpsでも作成可能</li>
                      <li>マルチクラウドGW契約期間中は常に利用できます</li>
                    </ul>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- 通常利用 -->
              <v-col cols="6">
                <v-card
                  variant="outlined"
                  class="h-100"
                  :color="isNormalUsage ? 'grey' : undefined"
                >
                  <v-card-text class="text-center">
                    <v-icon
                      icon="$mdiSitemapOutline"
                      size="50"
                      class="vnConnectType"
                    />

                    <div class="mt-2 font-weight-bold text-subtitle-1">
                      通常利用
                    </div>
                    <div class="text-body-2 mb-2">
                      契約内で利用可能な通常のVNコネクト
                    </div>

                    <v-btn
                      class="mt-1"
                      color="primary"
                      @click="normalRegister"
                      :disabled="isNormalUsage"
                    >
                      作成
                    </v-btn>

                    <ul class="ml-2 mt-2 text-left">
                      <li>KCPSに割り当てた帯域まで利用可能</li>
                    </ul>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { CloudLine } from "@/apis/CloudLineApi";
import MultiCloudKcps3VnLineEdit from "@/modals/multicloud/kcps3/v2/V2MultiCloudKcps3VnLineEdit.vue";

export default defineComponent({
  name: "CloudVnResettingList",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    /** KeyID */
    cloudVnKcpsKeyId: {
      type: String as PropType<string>,
      required: true,
      default: undefined,
    },
    /** 無料枠利用 */
    billingFreeUsage: {
      type: Boolean as PropType<boolean>,
      required: true,
      default: true,
    },
    /** クラウド回線の帯域最大値(品目の上限値制御用) */
    cloudLineMaxBandwidth: {
      type: Object as PropType<CloudLine["bandwidth"]>,
      required: true,
    },
    remainingBandwidth: {
      type: Number as PropType<number>,
      required: true,
    },
  },
  computed: {
    /** クラウド帯域値が0Mbpsの場合に通常枠制御 */
    isNormalUsage() {
      return this.cloudLineMaxBandwidth.value === 0;
    },
  },
  methods: {
    /** 無料枠作成 */
    async freeRegister() {
      await this.$modal.show(MultiCloudKcps3VnLineEdit, {
        cloudLineSeq: this.cloudLineSeq,
        cloudVnKcpsKeyId: this.cloudVnKcpsKeyId,
        billingFreeFlg: true,
        cloudLineMaxBandwidth: this.cloudLineMaxBandwidth,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** 通常作成 */
    async normalRegister() {
      await this.$modal.show(MultiCloudKcps3VnLineEdit, {
        cloudLineSeq: this.cloudLineSeq,
        cloudVnKcpsKeyId: this.cloudVnKcpsKeyId,
        cloudLineMaxBandwidth: this.cloudLineMaxBandwidth,
        remainingBandwidth: this.remainingBandwidth,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>

<style lang="scss" scoped>
.vnConnectType {
  color: #f5a623;
}

.freeBtnStyle {
  background-color: #f4a300;
  color: white;
}
</style>
